import { STORAGE_KEYS } from "@/lib/storage-keys";
import {
	cloudInventoryIsIncomplete,
	INCOMPLETE_INVENTORY_MESSAGE,
} from "./backup-inventory";

export type CloudBackupRefreshResult =
	| { status: "flushed" }
	| { status: "skipped"; reason: string }
	| { status: "failed"; error: string };

type FetchLike = (
	input: RequestInfo | URL,
	init?: RequestInit
) => Promise<Pick<Response, "ok" | "status" | "text">>;

interface CloudBackupRefreshDeps {
	fetch?: FetchLike;
	storage?: Pick<Storage, "getItem" | "setItem" | "removeItem">;
}

const STALE_VALUE = "1";
const cloudWrites = new WeakMap<object, Promise<unknown>>();

/** Order writes, including enablement, so a slow old request cannot finish last. */
function serializeCloudWrite(
	deps: CloudBackupRefreshDeps | undefined,
	operation: () => Promise<CloudBackupRefreshResult>
): Promise<CloudBackupRefreshResult> {
	const storage = getStorage(deps);
	if (!storage) {
		return Promise.resolve({
			status: "skipped",
			reason: "storage-unavailable",
		});
	}
	const expected = storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (expected && cloudInventoryIsIncomplete(expected, storage)) {
		return Promise.resolve({
			status: "skipped",
			reason: "incomplete-inventory",
		});
	}
	const run = async (): Promise<CloudBackupRefreshResult> => {
		if (expected && cloudInventoryIsIncomplete(expected, storage)) {
			return { status: "skipped", reason: "incomplete-inventory" };
		}
		if (storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== expected) {
			return { status: "skipped", reason: "superseded" };
		}
		return await operation();
	};
	return queueStorageWrite(storage, run, !deps?.storage);
}

function queueStorageWrite<T>(
	storage: object,
	run: () => Promise<T>,
	useOriginLock: boolean
): Promise<T> {
	const withOriginLock = async (): Promise<T> => {
		if (useOriginLock && typeof navigator !== "undefined" && navigator.locks) {
			return await navigator.locks.request("sigma-cloud-backup-write", run);
		}
		return await run();
	};
	const pending = (cloudWrites.get(storage) ?? Promise.resolve()).then(
		withOriginLock,
		withOriginLock
	);
	cloudWrites.set(
		storage,
		pending.catch(() => undefined)
	);
	return pending;
}

/** Preserve callers' response contracts while sharing ordering with automatic refresh. */
export function queueCloudBackupUpload<T>(
	encrypted: string,
	operation: () => Promise<T>
): Promise<T> {
	if (typeof localStorage === "undefined") {
		return operation();
	}
	const storage = localStorage;
	return queueStorageWrite(
		storage,
		async () => {
			if (cloudInventoryIsIncomplete(encrypted, storage)) {
				throw new Error(INCOMPLETE_INVENTORY_MESSAGE);
			}
			if (storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== encrypted) {
				throw new Error("Cloud upload was superseded by another backup");
			}
			return await operation();
		},
		true
	);
}

export function markNewWalletCloudBackupPending(
	bapId: string,
	deps?: CloudBackupRefreshDeps
): void {
	const storage = getStorage(deps);
	if (!storage) {
		return;
	}
	storage.setItem(STORAGE_KEYS.NEW_WALLET_CLOUD_BACKUP_PENDING, bapId);
}

export function getPendingNewWalletCloudBackupBapId(
	deps?: CloudBackupRefreshDeps
): string | null {
	const storage = getStorage(deps);
	if (!storage?.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP)) {
		return null;
	}
	return storage.getItem(STORAGE_KEYS.NEW_WALLET_CLOUD_BACKUP_PENDING);
}

function getStorage(
	deps?: CloudBackupRefreshDeps
): Pick<Storage, "getItem" | "setItem" | "removeItem"> | null {
	if (deps?.storage) {
		return deps.storage;
	}
	if (typeof localStorage === "undefined") {
		return null;
	}
	return localStorage;
}

function getFetch(deps?: CloudBackupRefreshDeps): FetchLike | null {
	if (deps?.fetch) {
		return deps.fetch;
	}
	if (typeof fetch === "undefined") {
		return null;
	}
	return fetch;
}

function errorMessage(error: unknown): string {
	return error instanceof Error ? error.message : String(error);
}

// Mark that the cloud copy no longer matches the local encrypted backup.
export function markCloudBackupStale(deps?: CloudBackupRefreshDeps): void {
	const storage = getStorage(deps);
	if (!storage) {
		console.error("[cloud-backup-refresh] Cannot mark stale: no localStorage");
		return;
	}
	storage.setItem(STORAGE_KEYS.CLOUD_BACKUP_STALE, STALE_VALUE);
}

// If marked stale and a cloud row already exists, refresh it with the current
// encrypted backup. A missing row keeps the mark so a future opted-in row can
// be refreshed without this mutation site running again.
async function flushCloudBackupIfStaleInternal(
	deps?: CloudBackupRefreshDeps
): Promise<CloudBackupRefreshResult> {
	const storage = getStorage(deps);
	if (!storage) {
		return { status: "skipped", reason: "storage-unavailable" };
	}

	if (!storage.getItem(STORAGE_KEYS.CLOUD_BACKUP_STALE)) {
		return { status: "skipped", reason: "not-stale" };
	}

	const encryptedBackup = storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encryptedBackup) {
		return { status: "skipped", reason: "no-local-backup" };
	}

	const fetcher = getFetch(deps);
	if (!fetcher) {
		return { status: "failed", error: "fetch unavailable" };
	}

	try {
		const existing = await fetcher("/api/backup", {
			method: "GET",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
		});

		if (existing.status === 404) {
			return { status: "skipped", reason: "no-cloud-backup" };
		}

		if (!existing.ok) {
			const error = await existing.text();
			console.error(
				"[cloud-backup-refresh] Failed to check cloud backup row:",
				error
			);
			return {
				status: "failed",
				error: error || `Cloud backup check failed (${existing.status})`,
			};
		}

		if (storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== encryptedBackup) {
			return { status: "skipped", reason: "superseded" };
		}
		const posted = await fetcher("/api/backup", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ encryptedBackup }),
		});

		if (!posted.ok) {
			const error = await posted.text();
			console.error(
				"[cloud-backup-refresh] Failed to refresh cloud backup:",
				error
			);
			return {
				status: "failed",
				error: error || `Cloud backup refresh failed (${posted.status})`,
			};
		}

		if (storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== encryptedBackup) {
			return { status: "skipped", reason: "superseded" };
		}
		storage.removeItem(STORAGE_KEYS.CLOUD_BACKUP_STALE);
		return { status: "flushed" };
	} catch (error) {
		const message = errorMessage(error);
		console.error("[cloud-backup-refresh] Refresh errored:", message);
		return { status: "failed", error: message };
	}
}

export function flushCloudBackupIfStale(
	deps?: CloudBackupRefreshDeps
): Promise<CloudBackupRefreshResult> {
	return serializeCloudWrite(deps, () => flushCloudBackupIfStaleInternal(deps));
}

export async function refreshCloudBackup(
	deps?: CloudBackupRefreshDeps
): Promise<CloudBackupRefreshResult> {
	markCloudBackupStale(deps);
	return await flushCloudBackupIfStale(deps);
}

/**
 * Create (or refresh) the cloud backup row unconditionally.
 *
 * flushCloudBackupIfStale only refreshes a row that already exists, which
 * keeps cloud backup opt-in for imported wallets. This is the enablement
 * path: new-wallet signup turns cloud backup on by default (OPL-4240), and
 * the dashboard's "Back up to cloud" action uses the same semantics. The
 * caller must have an authenticated session.
 */
async function enableCloudBackupInternal(
	deps?: CloudBackupRefreshDeps
): Promise<CloudBackupRefreshResult> {
	const storage = getStorage(deps);
	if (!storage) {
		return { status: "skipped", reason: "storage-unavailable" };
	}

	const encryptedBackup = storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!encryptedBackup) {
		return { status: "skipped", reason: "no-local-backup" };
	}

	const fetcher = getFetch(deps);
	if (!fetcher) {
		return { status: "failed", error: "fetch unavailable" };
	}

	try {
		const posted = await fetcher("/api/backup", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify({ encryptedBackup }),
		});

		if (!posted.ok) {
			const error = await posted.text();
			console.error(
				"[cloud-backup-refresh] Failed to enable cloud backup:",
				error
			);
			return {
				status: "failed",
				error: error || `Cloud backup enable failed (${posted.status})`,
			};
		}

		if (storage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !== encryptedBackup) {
			return { status: "skipped", reason: "superseded" };
		}
		storage.removeItem(STORAGE_KEYS.CLOUD_BACKUP_STALE);
		return { status: "flushed" };
	} catch (error) {
		const message = errorMessage(error);
		console.error("[cloud-backup-refresh] Enable errored:", message);
		return { status: "failed", error: message };
	}
}

export function enableCloudBackup(
	deps?: CloudBackupRefreshDeps
): Promise<CloudBackupRefreshResult> {
	return serializeCloudWrite(deps, () => enableCloudBackupInternal(deps));
}

/** Complete only the explicitly marked new-wallet default-on upload. */
export async function enableNewWalletCloudBackup(
	bapId: string,
	deps?: CloudBackupRefreshDeps
): Promise<CloudBackupRefreshResult> {
	const storage = getStorage(deps);
	if (
		!storage ||
		storage.getItem(STORAGE_KEYS.NEW_WALLET_CLOUD_BACKUP_PENDING) !== bapId
	) {
		return { status: "skipped", reason: "new-wallet-backup-not-pending" };
	}

	const result = await enableCloudBackup(deps);
	if (
		result.status === "flushed" &&
		storage.getItem(STORAGE_KEYS.NEW_WALLET_CLOUD_BACKUP_PENDING) === bapId
	) {
		storage.removeItem(STORAGE_KEYS.NEW_WALLET_CLOUD_BACKUP_PENDING);
	}
	return result;
}
